import { DispatcherResponse, CreateDispatcherData, UpdateDispatcherData } from './dispatcher.model';
import { twApi } from '../api';
import { BaseQueryParams } from '../../Global.model';

/**
 * Api endpoints for dispatchers
 */
const dispatcherApi = twApi.injectEndpoints({
  endpoints: (builder) => ({
    getDispatchers: builder.query<
      { content: DispatcherResponse[]; total: number },
      BaseQueryParams
    >({
      query: (arg) => ({
        url: 'contacts/dispatchers',
        params: { ...arg }
      }),
      transformResponse: (response: { data: DispatcherResponse[]; total: number }) => ({
        content: response.data,
        total: response.total
      }),
      providesTags: (result = { content: [], total: 0 }) =>
        result.content
          ? [
              ...result.content.map(({ id }) => ({ type: 'Dispatchers' as const, id })),
              { type: 'Dispatchers', id: 'DISPATCHERS_LIST' }
            ]
          : [{ type: 'Dispatchers', id: 'DISPATCHERS_LIST' }]
    }),
    getDispatcherById: builder.query<DispatcherResponse, string>({
      query: (id) => ({
        url: `contacts/dispatchers/${id}`
      }),
      transformResponse: (response: { data: DispatcherResponse }) => {
        return response.data;
      },
      providesTags: (result, error, id) => [{ type: 'Dispatchers' as const, id }]
    }),
    createDispatcher: builder.mutation<DispatcherResponse, CreateDispatcherData>({
      query: (dispatcher) => {
        const {
          first_name,
          last_name,
          pay_rate_type,
          pay_rate,
          email_address,
          phone,
          create_user_and_send_invite
        } = dispatcher;

        const params: CreateDispatcherData = {
          first_name: first_name,
          last_name: last_name
        };

        email_address ? (params.email_address = email_address) : null;
        phone ? (params.phone = phone) : null;
        pay_rate_type ? (params.pay_rate_type = pay_rate_type) : null;
        pay_rate ? (params.pay_rate = pay_rate) : null;
        create_user_and_send_invite
          ? (params.create_user_and_send_invite = create_user_and_send_invite)
          : null;

        return {
          url: 'contacts/dispatchers',
          method: 'POST',
          body: { ...params }
        };
      },
      transformResponse: (response: { data: DispatcherResponse }) => response.data,
      invalidatesTags: (result, error, arg) => [{ type: 'Dispatchers', id: 'DISPATCHERS_LIST' }]
    }),
    updateDispatcher: builder.mutation<DispatcherResponse, UpdateDispatcherData>({
      query: (dispatcher) => {
        const { email_address, phone, pay_rate, pay_rate_type, first_name, last_name, id } =
          dispatcher;

        const params: Partial<UpdateDispatcherData> = {};

        !!email_address ? (params.email_address = email_address) : null;
        !!phone ? (params.phone = phone.toString()) : null;
        !!first_name ? (params.first_name = first_name) : null;
        !!last_name ? (params.last_name = last_name) : null;
        !!pay_rate_type ? (params.pay_rate_type = pay_rate_type) : null;
        !!pay_rate ? (params.pay_rate = pay_rate) : null;

        return {
          url: `contacts/dispatchers/${id}`,
          method: 'PUT',
          body: { ...params }
        };
      },
      transformResponse: (response: { data: DispatcherResponse }) => response.data,
      invalidatesTags: (result, error, { id }) => [
        { type: 'Dispatchers', id },
        { type: 'Dispatchers', id: 'DISPATCHERS_LIST' }
      ]
    }),
    deleteDispatcher: builder.mutation<DispatcherResponse, string>({
      query: (id: string) => ({
        url: `contacts/dispatchers/${id}`,
        method: 'DELETE'
      }),
      onQueryStarted: (id, { dispatch, queryFulfilled }) => {
        const update = dispatch(
          dispatcherApi.util.updateQueryData('getDispatchers', {}, (dispatchersData) => {
            const index = dispatchersData.content.findIndex((item) => item.id === id);

            if (index !== -1) {
              dispatchersData.content.splice(index, 1);
              dispatchersData.total -= 1;
            }
          })
        );
        queryFulfilled.catch(() => {
          update.undo();
        });
      },
      invalidatesTags: (result, error, id) => [
        { type: 'Dispatchers', id },
        { type: 'Dispatchers', id: 'DISPATCHERS_LIST' }
      ]
    })
  })
});

export const {
  useGetDispatchersQuery,
  useGetDispatcherByIdQuery,
  useCreateDispatcherMutation,
  useUpdateDispatcherMutation,
  useDeleteDispatcherMutation
} = dispatcherApi;
